import { useState, useContext, useMemo, useEffect } from 'react';

import Price from 'types/Price';
import { ProductContext, ProductContextType } from '@providers/ProductProvider';
import { SessionContext } from '@providers/SessionProvider';

import ProductHead from './components/ProductHead';
import ProductView from './Product';

const { Provider } = ProductContext;

const getExtendedPrice = (
  variant: 'net' | 'gross',
  complementaryVariants: { price: Price; grossPrice: Price }[],
  basePrice: Price
): Price => ({
  amount:
    basePrice.amount +
    complementaryVariants.reduce(
      (acc, curr) => acc + (variant === 'net' ? curr.price.amount : curr.grossPrice.amount),
      0
    ),
  currency: basePrice.currency,
});

export type Props = Omit<
  ProductContextType,
  | 'selectedComplementaryProductsIds'
  | 'setSelectedComplementaryProductsIds'
  | 'anchorIndex'
  | 'setAnchorIndex'
  | 'sectionsVisibility'
  | 'grossPrice'
  | 'fullGrossPrice'
  | 'fullPrice'
  | 'currentSupplier'
> & {
  hits: number;
  grossPrice: number;
  currentSuppliers: [ProductContextType['currentSupplier']];
  isDescriptionNotEmpty: boolean;
};

const Product = ({ grossPrice, currentSuppliers, isDescriptionNotEmpty, hits, ...props }: Props) => {
  const { isLoggedIn } = useContext(SessionContext);
  const [selectedComplementaryProductsIds, setSelectedComplementaryProductsIds] = useState<number[]>([]);
  const [anchorIndex, setAnchorIndex] = useState(-1);

  const {
    id,
    names,
    video,
    attributesDescriptions,
    attributes,
    recommendedVariants,
    ratingCount,
    documents,
    price,
    complementaryVariants,
    variants,
  } = props;

  const sectionsVisibility = useMemo(
    () => ({
      description: !!(video || isDescriptionNotEmpty || attributesDescriptions.length),
      specification: !!attributes.length,
      recommendedVariants: !!recommendedVariants.length,
      variants: !!variants.filter(({ variant }) => variant.id !== id).length,
      rating: !!ratingCount || isLoggedIn(),
      documentation: !!documents.length,
    }),
    [id, isLoggedIn()] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const finalGrossPrice = { amount: grossPrice, currency: price.currency };

  const [fullPrice, fullGrossPrice] = useMemo(() => {
    const selectedComplementaryProducts = complementaryVariants.filter((complementaryVariant) =>
      selectedComplementaryProductsIds.includes(complementaryVariant.id)
    );

    const extendedNet = getExtendedPrice('net', selectedComplementaryProducts, price);
    const extendedGross = getExtendedPrice('gross', selectedComplementaryProducts, finalGrossPrice);

    return [
      extendedNet.amount > price.amount ? extendedNet : price,
      extendedGross.amount > finalGrossPrice.amount ? extendedGross : finalGrossPrice,
    ];
  }, [id, selectedComplementaryProductsIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedComplementaryProductsIds(
      selectedComplementaryProductsIds.filter((productId) =>
        complementaryVariants.find((complementaryVariant) => complementaryVariant.id === productId)
      )
    );
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Provider
      value={{
        ...props,
        names,
        anchorIndex,
        currentSupplier: currentSuppliers[0],
        grossPrice: finalGrossPrice,
        fullPrice,
        fullGrossPrice,
        selectedComplementaryProductsIds,
        sectionsVisibility,
        setAnchorIndex,
        setSelectedComplementaryProductsIds,
      }}
    >
      <ProductHead />
      <ProductView hits={hits} isDescriptionNotEmpty={isDescriptionNotEmpty} />
    </Provider>
  );
};

export default Product;
